"use client";

/**
 * Loader de boot full-screen de marca (FEATURE-013), sobre el **isotipo**.
 *
 * Qué hace: las tres masas del isotipo se encienden **en secuencia** —las dos de tinta
 * y después la verde— así el ojo lee el trazo avanzando en la dirección de la flecha.
 * Debajo corre una barra indeterminada, que es la misma gramática de «avance» que ya
 * usan la barra de goleador y la de fecha cargada.
 *
 * Presentacional puro: no lee SWR ni contexto de liga. Se autoabastece el tema
 * (`data-theme` + `--ld-accent`) desde props, así sirve dentro de `/leagues` (lo monta
 * `LeagueBootGate`) y también standalone en otras superficies públicas.
 *
 * - `accent`: color de la **barra de avance**. El isotipo NO lo toma: la marca mantiene
 *   su verde aunque la liga tenga otro acento. Default el verde de marca, que es el
 *   fallback cuando no hay acento de liga cacheado.
 * - `mode`: claro/oscuro para fondo y tinta.
 *
 * Animaciones (keyframes en globals.css): `ld-mark-in`, `ld-sweep`. Con
 * `prefers-reduced-motion: reduce` se detienen y la marca queda **completa** — el
 * keyframe arranca y termina en opacidad baja, así que depende de que la animación se
 * corte SIN `fill-mode: forwards`. No agregar `forwards` o el loader se apaga.
 *
 * Gotcha del color dentro de una isla de tema: el isotipo se pinta con `var(--ld-ink)`,
 * el token CRUDO, nunca con `var(--color-ink)`. `--color-*` viene de `@theme inline`, o
 * sea que Tailwind lo resuelve UNA vez en `:root` y lo emite ya sustituido; como las
 * custom properties se heredan por su valor computado, redeclarar `--ld-ink` acá no
 * lo vuelve a sustituir y la marca queda congelada en la tinta del tema por defecto
 * (casi blanca) — invisible en claro. Los tokens crudos sí se redeclaran por
 * `data-theme`. Regla general: dentro de una isla, `--ld-*`, jamás `--color-*`.
 */
import type { CSSProperties } from "react";
import { useTranslations } from "next-intl";
import { ISOTIPO, MARCA } from "@/components/ui/isotipo";

const GREEN = MARCA.verde;

/** Retardo por masa: la flecha se «dibuja» en el orden en que se lee. */
const RETARDO = [0, 0.16, 0.32];

export interface LeagueLoaderProps {
  mode?: "light" | "dark";
  accent?: string;
  message?: string;
  variant?: "fullscreen" | "inline";
}

export function LeagueLoader({
  mode = "dark",
  accent = GREEN,
  message,
  variant = "fullscreen",
}: LeagueLoaderProps) {
  const t = useTranslations("league");
  const label = message ?? t("loading.boot");

  const style = {
    "--ld-accent": accent,
    "--ld-glow": `color-mix(in srgb, ${accent} 30%, transparent)`,
  } as CSSProperties;

  const layout = variant === "fullscreen" ? "fixed inset-0 z-50" : "absolute inset-0";

  return (
    <div
      // Siempre explícito (no `undefined` en dark): el loader es una isla de tema propia y
      // NO debe heredar el data-theme del ancestro (p. ej. un shell de liga en light).
      data-theme={mode}
      style={style}
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={`${layout} grid place-items-center bg-bg`}
    >
      <div className="flex flex-col items-center gap-6">
        <svg viewBox={ISOTIPO.viewBox} className="h-[82px] w-[132px]" aria-hidden="true">
          {ISOTIPO.tinta.map((d, i) => (
            <path
              key={d.slice(0, 12)}
              d={d}
              fill="var(--ld-ink)"
              className="ld-mark-in"
              style={{ animationDelay: `${RETARDO[i]}s` }}
            />
          ))}
          {/* El verde de la marca es literal: no lo re-tintea el acento de la liga. */}
          <path
            d={ISOTIPO.verde}
            fill={GREEN}
            className="ld-mark-in"
            style={{ animationDelay: `${RETARDO[2]}s` }}
          />
        </svg>

        {/* Barra de avance indeterminada: el mismo lenguaje que las barras de dato. */}
        <span className="block h-1 w-32 overflow-hidden rounded-full bg-panel-2">
          <span className="ld-sweep block h-full w-1/3 rounded-full bg-accent" />
        </span>

        <p className="text-sm text-muted">{label}</p>
      </div>
    </div>
  );
}
